import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { customerAPI, getApiErrorMessage } from '@matix/api-client';
import { useAuth } from '@matix/auth';
import type { Bilingual, Country, Customer, CustomerAddress, AddressInput, GeoAddress } from '@matix/types';
import { useLang } from '../../lib/language';
import { useTenantConfig } from '../../store/tenantConfig';
import { CustomerMeasurementsModal } from './CustomerMeasurementsModal';
import { toast } from '../../store/toast';
import { CheckIcon, TrashIcon, PlusIcon } from '../../components/icons';
import { LookupCombo } from './LookupCombo';
import { AddressLookupPanel } from './AddressLookup';
import { matchLookupOption, pickName } from './addressMatch';
import { readAddressSeed, rememberAddressSeed, type AddressSeed } from './lastAddress';
import {
  useCustomerDetail,
  useVendorCategories,
  useCountries,
  useGovernorates,
  useDistricts,
  useBranchOptions,
  useCustomerBranchLinks,
} from './hooks';

/**
 * CustomerForm — full add/edit form for a cash customer, matching the legacy
 * app's field set:
 *   - identity: phone*, name, Arabic name, email
 *   - classification: category (GL_VendorCategory), tax reg no, birthday, gender
 *   - multiple addresses (area, country, block, street, avenue, house, floor,
 *     flat, note) with full add/edit/delete
 *   - branch visibility (pos_customer_branches) when the CustomerBranchLink
 *     flag is on — available on both add and edit
 *
 * Add posts to POST /customers/cash (+ POST /:id/branches). Edit PATCHes the
 * customer, reconciles addresses (add/update/delete), and saves branch links.
 * Only cash customers are editable here — credit (GL) customers live in the ERP.
 *
 * Country defaults to the tenant's own country (`default_country_id` from
 * /customers/countries) on every NEW address, and when that country is Saudi
 * Arabia the address card switches to the National Address layout — see
 * KsaAddressFields. Either layout can create a missing lookup value inline
 * (city/district on KSA, area everywhere else).
 *
 * A new address card is also pre-filled from the last address this cashier
 * saved on this branch — see ./lastAddress.
 */

let addrKeySeq = 0;
function nextAddrKey(): string {
  addrKeySeq += 1;
  return `new-${addrKeySeq}`;
}

interface AddressDraft {
  _key: string;
  /**
   * True while the card still carries values copied from the cashier's last
   * saved address. Purely a UI marker (drives the "prefilled" hint) — never
   * sent to the API.
   */
  _seeded?: boolean;
  /** Present for an existing address; absent for a freshly-added one. */
  id?: number;
  /** GL_Area — الحي (district) under the KSA layout. */
  area_id: number | null;
  /** GL_Governorate — المدينة (city). */
  governorate_id: number | null;
  country_id: number | null;
  avenue: string;
  block: string;
  street: string;
  /** House / رقم المبنى (building number) on the KSA layout. */
  house: string;
  floor: string;
  flat: string;
  /** الرقم الفرعي */
  secondary_number: string;
  /** الرمز البريدي */
  postal_code: string;
  /** العنوان المختصر */
  short_address_code: string;
  address_note: string;
}

function addrToDraft(a: CustomerAddress): AddressDraft {
  return {
    _key: `a${a.id}`,
    id: a.id,
    area_id: a.area_id ?? null,
    governorate_id: a.governorate_id ?? null,
    country_id: a.country_id ?? null,
    avenue: a.avenue ?? '',
    block: a.block ?? '',
    street: a.street ?? '',
    house: a.house ?? '',
    floor: a.floor ?? '',
    flat: a.flat ?? '',
    secondary_number: a.secondary_number ?? '',
    postal_code: a.postal_code ?? '',
    short_address_code: a.short_address_code ?? '',
    address_note: a.address_note ?? '',
  };
}

/**
 * A blank address, pre-set to the tenant's country. Only NEW addresses get the
 * default — an existing row with no country stays as it was saved rather than
 * being silently rewritten on open.
 *
 * `seed` (the cashier's last saved address, if any) fills in the location half
 * of the card. The building-identifying fields deliberately stay empty — see
 * ./lastAddress.
 */
function emptyAddr(defaultCountryId: number | null, seed?: AddressSeed | null): AddressDraft {
  return {
    _key: nextAddrKey(),
    _seeded: seed ? true : undefined,
    area_id: seed?.area_id ?? null,
    governorate_id: seed?.governorate_id ?? null,
    country_id: seed?.country_id ?? defaultCountryId,
    avenue: seed?.avenue ?? '',
    block: seed?.block ?? '',
    street: seed?.street ?? '',
    house: '',
    floor: '',
    flat: '',
    secondary_number: '',
    postal_code: '',
    short_address_code: '',
    address_note: '',
  };
}

/** The half of a draft that is worth carrying to the next new address. */
function toSeed(a: AddressDraft): AddressSeed {
  return {
    country_id: a.country_id ?? null,
    governorate_id: a.governorate_id ?? null,
    area_id: a.area_id ?? null,
    avenue: a.avenue,
    block: a.block,
    street: a.street,
  };
}

const ADDR_TEXT_FIELDS = [
  'avenue',
  'block',
  'street',
  'house',
  'floor',
  'flat',
  'secondary_number',
  'postal_code',
  'short_address_code',
  'address_note',
] as const;

/**
 * At least one meaningful field → worth persisting. The country alone doesn't
 * count once it is auto-filled: an untouched blank card would otherwise be
 * saved as an address containing nothing but "Saudi Arabia".
 */
function addrMeaningful(a: AddressDraft): boolean {
  return Boolean(
    a.area_id || a.governorate_id || ADDR_TEXT_FIELDS.some((f) => a[f] && a[f].trim()),
  );
}

/** Stable signature to detect whether an existing address actually changed. */
function addrSig(a: AddressDraft): string {
  return JSON.stringify({
    area_id: a.area_id ?? null,
    governorate_id: a.governorate_id ?? null,
    country_id: a.country_id ?? null,
    ...Object.fromEntries(ADDR_TEXT_FIELDS.map((f) => [f, a[f].trim()])),
  });
}

function toAddressInput(a: AddressDraft): AddressInput {
  const v = (x: string) => {
    const t = x.trim();
    return t || null;
  };
  return {
    area_id: a.area_id ?? null,
    governorate_id: a.governorate_id ?? null,
    country_id: a.country_id ?? null,
    avenue: v(a.avenue),
    block: v(a.block),
    street: v(a.street),
    house: v(a.house),
    floor: v(a.floor),
    flat: v(a.flat),
    secondary_number: v(a.secondary_number),
    postal_code: v(a.postal_code),
    short_address_code: v(a.short_address_code),
    address_note: v(a.address_note),
  };
}

// ── Saudi National Address validation ────────────────────────────────────
// Formats per the Saudi Post / SPL spec shown on the customer's address card:
//   رقم المبنى (building)   4 digits      → 8127
//   الرقم الفرعي (secondary) 4 digits      → 3330
//   الرمز البريدي (postal)   5 digits      → 13214
//   العنوان المختصر (short)  4 letters + 4 digits → RFQB8127
// Every field stays optional; a value is only rejected once it's non-empty and
// clearly malformed, so a half-known address can still be saved.
const KSA_FOUR_DIGITS = /^\d{4}$/;
const KSA_POSTAL = /^\d{5}$/;
const KSA_SHORT_CODE = /^[A-Za-z]{4}\d{4}$/;

type AddrFieldErrors = Partial<Record<keyof AddressDraft, string>>;

function validateKsaAddress(
  a: AddressDraft,
  tr: (v: { en: string; ar: string }) => string,
): AddrFieldErrors {
  const errors: AddrFieldErrors = {};
  const digits = (n: number) =>
    tr({ en: `Must be ${n} digits`, ar: `يجب أن يتكون من ${n} أرقام` });

  if (a.house.trim() && !KSA_FOUR_DIGITS.test(a.house.trim())) errors.house = digits(4);
  if (a.secondary_number.trim() && !KSA_FOUR_DIGITS.test(a.secondary_number.trim())) {
    errors.secondary_number = digits(4);
  }
  if (a.postal_code.trim() && !KSA_POSTAL.test(a.postal_code.trim())) {
    errors.postal_code = digits(5);
  }
  if (a.short_address_code.trim() && !KSA_SHORT_CODE.test(a.short_address_code.trim())) {
    errors.short_address_code = tr({
      en: '4 letters + 4 digits, e.g. RFQB8127',
      ar: '٤ أحرف + ٤ أرقام، مثال RFQB8127',
    });
  }
  return errors;
}

// ── Outer: resolves the customer detail for edit before rendering the form ──

export function CustomerForm({
  mode,
  customerId,
  initialPhone = '',
  initialName = '',
  onCancel,
  onSaved,
}: {
  mode: 'add' | 'edit';
  customerId: number | null;
  initialPhone?: string;
  initialName?: string;
  onCancel: () => void;
  onSaved: (id: number) => void;
}) {
  const { t: tr } = useLang();
  const { data: loaded, isLoading } = useCustomerDetail(
    mode === 'edit' ? customerId : null,
    'cash',
  );

  if (mode === 'edit') {
    if (isLoading || !loaded) {
      return (
        <>
          <FormHeader
            title={tr({ en: 'Edit customer', ar: 'تعديل العميل' })}
            sub={tr({ en: 'Loading…', ar: 'جارٍ التحميل…' })}
            onBack={onCancel}
          />
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            {tr({ en: 'Loading customer…', ar: 'جارٍ تحميل العميل…' })}
          </div>
        </>
      );
    }
    return (
      <CustomerFormInner
        key={`edit-${loaded.id}`}
        mode="edit"
        customerId={loaded.id}
        initial={loaded}
        onCancel={onCancel}
        onSaved={onSaved}
      />
    );
  }

  return (
    <CustomerFormInner
      key="add"
      mode="add"
      customerId={null}
      initialPhone={initialPhone}
      initialName={initialName}
      onCancel={onCancel}
      onSaved={onSaved}
    />
  );
}

// ── Inner: holds all form state, seeded from `initial` (edit) or blank (add) ──

function CustomerFormInner({
  mode,
  customerId,
  initial,
  initialPhone = '',
  initialName = '',
  onCancel,
  onSaved,
}: {
  mode: 'add' | 'edit';
  customerId: number | null;
  initial?: Customer;
  initialPhone?: string;
  initialName?: string;
  onCancel: () => void;
  onSaved: (id: number) => void;
}) {
  const { t: tr } = useLang();
  const queryClient = useQueryClient();
  const { cashier } = useAuth();
  const branchLinkEnabled = useTenantConfig((s) => s.customerBranchLink);
  // POS_CustomerMeasurements — offered once the customer exists (edit mode).
  const measurementsOn = useTenantConfig((s) => s.customerMeasurements);
  const [measuresOpen, setMeasuresOpen] = useState(false);
  // The cashier's current branch (from the session JWT). New customers default
  // to being visible in this branch and can't be saved branch-less.
  const currentBranchId = cashier?.branch_id ?? null;

  const [phone, setPhone] = useState(initial?.phone ?? initialPhone);
  const [name, setName] = useState(initial?.name ?? initialName);
  const [nameAr, setNameAr] = useState(initial?.name_ar ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [categoryId, setCategoryId] = useState<number | null>(initial?.category_id ?? null);
  const [taxRegNo, setTaxRegNo] = useState(initial?.tax_reg_no ?? '');
  const [dob, setDob] = useState(initial?.date_of_birth ?? '');
  const [genderId, setGenderId] = useState<number | null>(initial?.gender_id ?? null);
  const [addresses, setAddresses] = useState<AddressDraft[]>(
    (initial?.addresses ?? []).map(addrToDraft),
  );
  const [submitting, setSubmitting] = useState(false);

  // The last address this cashier saved on this branch, read once on mount so
  // it can't shift under the form while it's open.
  const [addressSeed] = useState<AddressSeed | null>(() => readAddressSeed(currentBranchId));

  // Original addresses (edit) — used to diff on save.
  const originalAddresses = useRef<AddressDraft[]>(
    (initial?.addresses ?? []).map(addrToDraft),
  );

  // The server's answer when DontAllowCustPhoneRepeat rejected this phone —
  // the ERP's own switch, so it can be on for one tenant and off for the next.
  // Shown ON THE FIELD rather than only as a toast: the cashier has just filled
  // a whole form and needs to see which box to change, and the message names
  // the customer that already holds the number.
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Per-address validation errors, keyed by the draft's _key. Only the KSA
  // layout produces any today; cleared as soon as the offending field changes.
  const [addrErrors, setAddrErrors] = useState<Record<string, AddrFieldErrors>>({});

  // Lookups (fetched once, cached). Categories/countries gated to when needed.
  const { data: categories } = useVendorCategories();
  const { data: countries, defaultCountryId } = useCountries();

  // Back-fill the country on address cards that were opened before the country
  // list resolved — the "add address" button can beat the lookup on a cold
  // cache, and a card left on "— None —" would never show the KSA layout.
  const countrySeeded = useRef(false);
  useEffect(() => {
    if (countrySeeded.current || defaultCountryId == null) return;
    countrySeeded.current = true;
    setAddresses((prev) =>
      prev.map((a) => (a.id == null && a.country_id == null ? { ...a, country_id: defaultCountryId } : a)),
    );
  }, [defaultCountryId]);

  // Branch visibility.
  const { data: branchOptions } = useBranchOptions(branchLinkEnabled);
  const linksQuery = useCustomerBranchLinks(
    branchLinkEnabled && mode === 'edit' ? customerId : null,
    'cash',
  );
  const [branchSelected, setBranchSelected] = useState<Set<number>>(new Set());
  const [branchTouched, setBranchTouched] = useState(false);
  const branchSeeded = useRef(false);
  useEffect(() => {
    if (branchSeeded.current) return;
    if (mode === 'edit') {
      // Edit: seed from the customer's existing links once they load.
      if (linksQuery.data) {
        setBranchSelected(new Set(linksQuery.data.branch_ids));
        branchSeeded.current = true;
      }
    } else if (branchLinkEnabled) {
      // Add: default to the cashier's current branch so the customer is scoped
      // here out of the box (and never accidentally saved visible-everywhere).
      if (currentBranchId != null) setBranchSelected(new Set([currentBranchId]));
      branchSeeded.current = true;
    }
  }, [mode, linksQuery.data, branchLinkEnabled, currentBranchId]);

  const phoneRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!phone) phoneRef.current?.focus();
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function patchAddr(key: string, next: Partial<AddressDraft>) {
    setAddresses((prev) => prev.map((a) => (a._key === key ? { ...a, ...next } : a)));
    // Editing a field clears its error so the message doesn't linger while the
    // cashier is mid-correction.
    setAddrErrors((prev) => {
      const forKey = prev[key];
      if (!forKey) return prev;
      const remaining = { ...forKey };
      for (const field of Object.keys(next) as Array<keyof AddressDraft>) delete remaining[field];
      return { ...prev, [key]: remaining };
    });
  }
  function removeAddr(key: string) {
    setAddresses((prev) => prev.filter((a) => a._key !== key));
    setAddrErrors((prev) => {
      const { [key]: _dropped, ...rest } = prev;
      return rest;
    });
  }
  function addAddr() {
    setAddresses((prev) => [...prev, emptyAddr(defaultCountryId, addressSeed)]);
  }
  /** Drop the pre-filled values and start this card from scratch. */
  function clearSeed(key: string) {
    setAddresses((prev) =>
      prev.map((a) =>
        a._key === key
          ? {
              ...a,
              _seeded: false,
              country_id: defaultCountryId,
              governorate_id: null,
              area_id: null,
              avenue: '',
              block: '',
              street: '',
            }
          : a,
      ),
    );
  }
  function toggleBranch(id: number) {
    setBranchTouched(true);
    setBranchSelected((prev) => {
      const nextSet = new Set(prev);
      if (nextSet.has(id)) nextSet.delete(id);
      else nextSet.add(id);
      return nextSet;
    });
  }

  // Save the branch links, tolerating a not-provisioned tenant (503).
  async function persistBranchLinks(id: number): Promise<void> {
    if (!branchLinkEnabled) return;
    if (mode === 'add' && branchSelected.size === 0) return; // nothing to link
    if (mode === 'edit' && !branchTouched) return; // unchanged
    try {
      await customerAPI.saveBranchLinks(id, 'cash', [...branchSelected]);
    } catch (err) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      if (ax.response?.status === 503) {
        toast(
          tr({
            en: 'Customer saved, but branch links are not enabled for this tenant yet',
            ar: 'تم حفظ العميل، لكن ربط الفروع غير مُفعّل لهذا الحساب بعد',
          }),
          'warning',
        );
      } else {
        toast(
          tr({ en: 'Customer saved, but branch links failed', ar: 'تم حفظ العميل، لكن فشل ربط الفروع' }),
          'warning',
        );
      }
    }
  }

  async function reconcileAddresses(id: number): Promise<void> {
    const origById = new Map(
      originalAddresses.current.filter((a) => a.id != null).map((a) => [a.id!, a]),
    );
    const currentIds = new Set(addresses.filter((a) => a.id != null).map((a) => a.id!));

    // Deletes: originals no longer present in the form.
    for (const [addrId] of origById) {
      if (!currentIds.has(addrId)) {
        await customerAPI.deleteAddress(id, addrId);
      }
    }

    // Updates + creates.
    for (const a of addresses) {
      if (a.id != null) {
        const orig = origById.get(a.id);
        if (!orig) continue;
        if (!addrMeaningful(a)) {
          // Cleared out → treat as a delete.
          await customerAPI.deleteAddress(id, a.id);
        } else if (addrSig(orig) !== addrSig(a)) {
          await customerAPI.updateAddress(id, a.id, toAddressInput(a));
        }
      } else if (addrMeaningful(a)) {
        await customerAPI.addAddress(id, toAddressInput(a));
      }
    }
  }

  async function submit() {
    if (!phone.trim()) {
      toast(tr({ en: 'Phone number is required', ar: 'رقم الهاتف مطلوب' }), 'error');
      phoneRef.current?.focus();
      return;
    }
    // A customer must be visible in at least one branch — otherwise it would be
    // scoped to nothing or fall back to "visible everywhere". Enforced on both
    // add and edit, when the branch-link feature is on and the subsidiary has
    // branches. In edit mode we wait for the existing links to load first, so we
    // never reject before knowing which branches the customer already has.
    const branchLinksLoading = mode === 'edit' && linksQuery.isLoading;
    if (
      branchLinkEnabled &&
      branchOptions.length > 0 &&
      branchSelected.size === 0 &&
      !branchLinksLoading
    ) {
      toast(
        tr({ en: 'Select at least one branch for this customer', ar: 'اختر فرعًا واحدًا على الأقل لهذا العميل' }),
        'error',
      );
      return;
    }

    // Saudi National Address formats. Only the addresses actually on a KSA
    // country are checked; everything else keeps the legacy free-text rules.
    const ksaCountryIds = new Set(countries.filter((c) => c.is_ksa).map((c) => c.id));
    const nextErrors: Record<string, AddrFieldErrors> = {};
    for (const a of addresses) {
      if (a.country_id == null || !ksaCountryIds.has(a.country_id)) continue;
      const errs = validateKsaAddress(a, tr);
      if (Object.keys(errs).length > 0) nextErrors[a._key] = errs;
    }
    setAddrErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast(
        tr({ en: 'Check the highlighted address fields', ar: 'راجع حقول العنوان المُظللة' }),
        'error',
      );
      return;
    }

    setPhoneError(null);
    setSubmitting(true);
    try {
      let savedId: number;
      if (mode === 'add') {
        const created = await customerAPI.createCash({
          phone: phone.trim(),
          name: name.trim() || undefined,
          name_ar: nameAr.trim() || null,
          email: email.trim() || null,
          category_id: categoryId ?? null,
          tax_reg_no: taxRegNo.trim() || null,
          date_of_birth: dob || null,
          gender_id: genderId ?? null,
          addresses: addresses.filter(addrMeaningful).map(toAddressInput),
        });
        savedId = created.id;
      } else {
        await customerAPI.updateCash(customerId!, {
          phone: phone.trim(),
          name: name.trim() || null,
          name_ar: nameAr.trim() || null,
          email: email.trim() || null,
          category_id: categoryId ?? null,
          tax_reg_no: taxRegNo.trim() || null,
          date_of_birth: dob || null,
          gender_id: genderId ?? null,
        });
        await reconcileAddresses(customerId!);
        savedId = customerId!;
      }

      await persistBranchLinks(savedId);

      // Remember the last address the cashier actually typed here, so the next
      // customer's address card opens on the same city/street. Only freshly
      // added rows count — re-saving an existing customer without touching the
      // addresses shouldn't rewrite the suggestion.
      const lastNew = [...addresses].reverse().find((a) => a.id == null && addrMeaningful(a));
      if (lastNew) rememberAddressSeed(currentBranchId, toSeed(lastNew));

      // Refresh every customer view (search list, this detail, branch links).
      await queryClient.invalidateQueries({ queryKey: ['customers'] });

      toast(
        mode === 'add'
          ? tr({ en: 'Customer added', ar: 'تمت إضافة العميل' })
          : tr({ en: 'Customer updated', ar: 'تم تحديث العميل' }),
        'success',
      );
      onSaved(savedId);
    } catch (err) {
      const ax = err as {
        response?: { status?: number; data?: { code?: string; error?: string } };
      };
      const status = ax.response?.status;
      const code = ax.response?.data?.code;
      const message = ax.response?.data?.error ?? getApiErrorMessage(err);
      if (status === 409 && code === 'PHONE_EXISTS') {
        setPhoneError(message);
        phoneRef.current?.focus();
        toast(message, 'error');
      } else {
        toast(message || tr({ en: 'Failed to save customer', ar: 'تعذّر حفظ العميل' }), 'error');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <FormHeader
        title={
          mode === 'add'
            ? tr({ en: 'Add new customer', ar: 'إضافة عميل جديد' })
            : tr({ en: 'Edit customer', ar: 'تعديل العميل' })
        }
        sub={tr({ en: 'Phone is required. All other fields are optional.', ar: 'الهاتف مطلوب. باقي الحقول اختيارية.' })}
        onBack={onCancel}
      />

      {/* Height is bounded by the modal's flex column (.modal caps at 88vh),
          so the footer with Save/Cancel stays on screen on short viewports. */}
      <div className="cust-add-form">
        {/* ── Identity ─────────────────────────────────────────────── */}
        <div className="add-form-grid">
          <div className="add-field">
            <label className="add-label">
              {tr({ en: 'Phone number', ar: 'رقم الهاتف' })} <span className="req">*</span>
            </label>
            <input
              ref={phoneRef}
              type="tel"
              className={`add-input${phoneError ? ' has-error' : ''}`}
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setPhoneError(null); }}
              placeholder="+966 5X XXX XXXX"
              autoComplete="off"
              disabled={submitting}
            />
            {phoneError && <div className="add-field-error">{phoneError}</div>}
          </div>
          <div className="add-field">
            <label className="add-label">{tr({ en: 'Name', ar: 'الاسم' })}</label>
            <input
              type="text"
              className="add-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mohamed Al-Rashid"
              autoComplete="off"
              disabled={submitting}
            />
          </div>
          <div className="add-field">
            <label className="add-label">{tr({ en: 'Name in Arabic', ar: 'الاسم بالعربية' })}</label>
            <input
              type="text"
              className="add-input ar"
              value={nameAr}
              onChange={(e) => setNameAr(e.target.value)}
              placeholder="محمد الراشد"
              dir="rtl"
              autoComplete="off"
              disabled={submitting}
            />
          </div>
          <div className="add-field">
            <label className="add-label">{tr({ en: 'Email', ar: 'البريد الإلكتروني' })}</label>
            <input
              type="email"
              className="add-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              autoComplete="off"
              disabled={submitting}
            />
          </div>

          {/* ── Classification ─────────────────────────────────────── */}
          <div className="add-field">
            <label className="add-label">{tr({ en: 'Category', ar: 'التصنيف' })}</label>
            <select
              className="add-input"
              value={categoryId ?? ''}
              onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : null)}
              disabled={submitting}
            >
              <option value="">{tr({ en: '— None —', ar: '— بدون —' })}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.name_ar || `#${c.id}`}
                </option>
              ))}
            </select>
          </div>
          <div className="add-field">
            <label className="add-label">{tr({ en: 'Tax reg. number', ar: 'الرقم الضريبي' })}</label>
            <input
              type="text"
              className="add-input"
              value={taxRegNo}
              onChange={(e) => setTaxRegNo(e.target.value)}
              autoComplete="off"
              disabled={submitting}
            />
          </div>
          <div className="add-field">
            <label className="add-label">{tr({ en: 'Birthday', ar: 'تاريخ الميلاد' })}</label>
            <input
              type="date"
              className="add-input"
              value={dob ?? ''}
              onChange={(e) => setDob(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="add-field">
            <label className="add-label">{tr({ en: 'Gender', ar: 'النوع' })}</label>
            <select
              className="add-input"
              value={genderId ?? ''}
              onChange={(e) => setGenderId(e.target.value ? Number(e.target.value) : null)}
              disabled={submitting}
            >
              <option value="">{tr({ en: '— Not set —', ar: '— غير محدد —' })}</option>
              <option value={1}>{tr({ en: 'Male', ar: 'ذكر' })}</option>
              <option value={2}>{tr({ en: 'Female', ar: 'أنثى' })}</option>
            </select>
          </div>
        </div>

        {/* ── Addresses ────────────────────────────────────────────── */}
        <div className="cust-form-section">
          <div className="cust-form-section-head">
            <span className="cust-section-title">{tr({ en: 'Addresses', ar: 'العناوين' })}</span>
            <button type="button" className="btn-secondary cust-addr-add" onClick={addAddr} disabled={submitting}>
              <PlusIcon size={14} /> {tr({ en: 'Add address', ar: 'إضافة عنوان' })}
            </button>
          </div>

          {addresses.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '4px 0 8px' }}>
              {tr({ en: 'No addresses. Add one for delivery orders.', ar: 'لا توجد عناوين. أضف عنوانًا لطلبات التوصيل.' })}
            </div>
          )}

          {addresses.map((a, idx) => (
            <AddressCard
              key={a._key}
              draft={a}
              index={idx}
              countries={countries}
              errors={addrErrors[a._key] ?? {}}
              disabled={submitting}
              onPatch={(next) => patchAddr(a._key, next)}
              onRemove={() => removeAddr(a._key)}
              onClearSeed={() => clearSeed(a._key)}
            />
          ))}
        </div>

        {/* ── Measurements (POS_CustomerMeasurements) ──────────────── */}
        {measurementsOn && mode === 'edit' && customerId != null && (
          <div className="cust-form-section">
            <span className="cust-section-title">{tr({ en: 'Measurements', ar: 'المقاسات' })}</span>
            <div style={{ marginTop: 6 }}>
              <button type="button" className="btn-secondary" onClick={() => setMeasuresOpen(true)}>
                {tr({ en: 'Open measurements', ar: 'فتح المقاسات' })}
              </button>
            </div>
            {measuresOpen && (
              <CustomerMeasurementsModal
                customerType="cash"
                customerId={customerId}
                customerName={name || null}
                onClose={() => setMeasuresOpen(false)}
              />
            )}
          </div>
        )}
        {measurementsOn && mode === 'add' && (
          <div className="cust-form-section">
            <span className="cust-section-title">{tr({ en: 'Measurements', ar: 'المقاسات' })}</span>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              {tr({
                en: 'Save the customer first, then add the measurements from the customer card.',
                ar: 'احفظ العميل أولاً، ثم أضف المقاسات من بطاقة العميل.',
              })}
            </div>
          </div>
        )}

        {/* ── Branch visibility ────────────────────────────────────── */}
        {branchLinkEnabled && (
          <div className="cust-form-section">
            <span className="cust-section-title">
              {tr({ en: 'Branch visibility', ar: 'ظهور الفروع' })} <span className="req">*</span>
            </span>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 8px' }}>
              {branchSelected.size === 0
                ? tr({ en: 'Select at least one branch — the customer is only visible in the branches you pick.', ar: 'اختر فرعًا واحدًا على الأقل — يظهر العميل فقط في الفروع التي تختارها.' })
                : tr({ en: `Visible only in ${branchSelected.size} branch(es).`, ar: `يظهر فقط في ${branchSelected.size} فرع/فروع.` })}
            </div>
            {branchOptions.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{tr({ en: 'No branches found', ar: 'لا توجد فروع' })}</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {branchOptions.map((b) => {
                  const on = branchSelected.has(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleBranch(b.id)}
                      disabled={submitting}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 13,
                        border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                        background: on ? 'var(--primary-light)' : 'var(--surface)',
                        color: on ? 'var(--primary)' : 'var(--text)',
                        fontWeight: on ? 600 : 400,
                      }}
                    >
                      {on && <CheckIcon size={13} />}
                      {b.name || b.name_ar || `#${b.id}`}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn-secondary" onClick={onCancel} disabled={submitting}>
          {tr({ en: 'Cancel', ar: 'إلغاء' })}
        </button>
        <div className="modal-actions">
          <button className="btn-primary-modal" onClick={submit} disabled={submitting}>
            <CheckIcon size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {submitting
              ? tr({ en: 'Saving…', ar: 'جارٍ الحفظ…' })
              : mode === 'add'
                ? tr({ en: 'Save customer', ar: 'حفظ العميل' })
                : tr({ en: 'Save changes', ar: 'حفظ التغييرات' })}
          </button>
        </div>
      </div>
    </>
  );
}

// ── One address card ─────────────────────────────────────────────────────

/**
 * A single address. The country dropdown decides the layout: Saudi Arabia gets
 * the National Address fields (المدينة/الحي/الشارع/رقم المبنى/الرقم الفرعي/
 * الرمز البريدي), everything else keeps the legacy Gulf set (Area/Block/
 * Street/Avenue/House). Floor and Flat are shown either way — the national
 * address omits them, but a delivery driver still needs the unit.
 *
 * The card opens with the address lookup (AddressLookupPanel): the National
 * Address short code on a Saudi address, a free-text map search anywhere else.
 * It sits first and is visually set apart because it is the fastest way to fill
 * the whole card — one code instead of six fields.
 *
 * Both layouts can create a missing lookup value inline: city + district on
 * the KSA layout, area everywhere else. Those tables are per-tenant reference
 * data that is never complete, and that is just as true outside Saudi Arabia.
 */
function AddressCard({
  draft: a,
  index,
  countries,
  errors,
  disabled,
  onPatch,
  onRemove,
  onClearSeed,
}: {
  draft: AddressDraft;
  index: number;
  countries: Country[];
  errors: AddrFieldErrors;
  disabled: boolean;
  onPatch: (next: Partial<AddressDraft>) => void;
  onRemove: () => void;
  onClearSeed: () => void;
}) {
  const { t: tr, lang } = useLang();

  const country = useMemo(
    () => countries.find((c) => c.id === a.country_id) ?? null,
    [countries, a.country_id],
  );
  const isKsa = Boolean(country?.is_ksa);
  const regionCode = country?.region_code ?? null;

  // The address the map lookup last resolved for THIS card. Held in component
  // state only — Inv_CashCustomerDetail has no lat/lng columns, so the pin
  // belongs to the lookup, not to the saved address.
  const [geo, setGeo] = useState<GeoAddress | null>(null);
  // Bumped on every successful lookup so CityAreaFields can tell a fresh result
  // from a re-render and only auto-fill the combos once per lookup.
  const [geoSeq, setGeoSeq] = useState(0);

  /**
   * Push a resolved address onto the card.
   *
   * Only fields Google actually answered are written — an empty result must not
   * wipe what the cashier typed. Values that would fail the Saudi format checks
   * are skipped rather than pasted in to blow up on save: Google returns a
   * building number for plenty of addresses that is not a 4-digit KSA one.
   * City and district are NOT set here; they are foreign keys, and matching
   * them against the tenant's own rows is CityAreaFields' job.
   */
  function applyGeo(addr: GeoAddress) {
    const next: Partial<AddressDraft> = { _seeded: false };
    const street = pickName(addr.street, lang);
    if (street) next.street = street;
    if (addr.building_number && (!isKsa || KSA_FOUR_DIGITS.test(addr.building_number))) {
      next.house = addr.building_number;
    }
    if (addr.postal_code && (!isKsa || KSA_POSTAL.test(addr.postal_code))) {
      next.postal_code = addr.postal_code;
    }
    if (addr.secondary_number) next.secondary_number = addr.secondary_number;
    if (addr.short_address_code) next.short_address_code = addr.short_address_code;
    onPatch(next);
    setGeo(addr);
    setGeoSeq((s) => s + 1);
  }

  return (
    <div className="cust-addr-card">
      <div className="cust-addr-card-head">
        <span className="cust-addr-card-title">
          {tr({ en: `Address ${index + 1}`, ar: `العنوان ${index + 1}` })}
        </span>
        <button
          type="button"
          className="cust-addr-remove"
          onClick={onRemove}
          disabled={disabled}
          aria-label={tr({ en: 'Remove address', ar: 'إزالة العنوان' })}
        >
          <TrashIcon size={14} />
        </button>
      </div>

      {a._seeded && (
        <div className="cust-addr-seeded">
          <span>
            {tr({
              en: 'Filled in from your last saved address — check it before saving.',
              ar: 'تم ملؤه من آخر عنوان حفظته — راجعه قبل الحفظ.',
            })}
          </span>
          <button type="button" onClick={onClearSeed} disabled={disabled}>
            {tr({ en: 'Clear', ar: 'مسح' })}
          </button>
        </div>
      )}

      {/* The lookup comes first: on a Saudi address one code fills the card. */}
      <AddressLookupPanel
        isKsa={isKsa}
        regionCode={regionCode}
        code={a.short_address_code}
        onCodeChange={(v) => onPatch({ short_address_code: v })}
        codeError={errors.short_address_code}
        disabled={disabled}
        onResolved={applyGeo}
        onCleared={() => setGeo(null)}
        result={geo}
      />

      <div className="add-form-grid">
        <div className="add-field">
          <label className="add-label">{tr({ en: 'Country', ar: 'الدولة' })}</label>
          <select
            className="add-input"
            value={a.country_id ?? ''}
            onChange={(e) =>
              onPatch({ country_id: e.target.value ? Number(e.target.value) : null })
            }
            disabled={disabled}
          >
            <option value="">{tr({ en: '— None —', ar: '— بدون —' })}</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.name_ar || `#${c.id}`}
              </option>
            ))}
          </select>
        </div>

        {isKsa ? (
          <KsaAddressFields
            draft={a}
            errors={errors}
            disabled={disabled}
            onPatch={onPatch}
            geo={geo}
            geoSeq={geoSeq}
          />
        ) : (
          <LegacyAddressFields
            draft={a}
            disabled={disabled}
            onPatch={onPatch}
            geo={geo}
            geoSeq={geoSeq}
          />
        )}

        <AddrText label={tr({ en: 'Floor', ar: 'الطابق' })} value={a.floor} onChange={(v) => onPatch({ floor: v })} disabled={disabled} />
        <AddrText label={tr({ en: 'Flat', ar: 'الشقة' })} value={a.flat} onChange={(v) => onPatch({ flat: v })} disabled={disabled} />
      </div>

      <div className="add-field" style={{ marginTop: 8 }}>
        <label className="add-label">{tr({ en: 'Address note', ar: 'ملاحظة العنوان' })}</label>
        <textarea
          className="add-input"
          rows={2}
          value={a.address_note}
          onChange={(e) => onPatch({ address_note: e.target.value })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

/**
 * The legacy Gulf address block, used for every country except Saudi Arabia:
 * City → Area, then Block / Street / Avenue / House.
 *
 * The City field used to be Saudi-only, which left this layout posting an area
 * with no parent — and `GL_Area.GovernorateID` is NOT NULL on every tenant, so
 * "add area" 500'd on GL_WEB_villashams_test. The city is now asked for here
 * too, which is also what the cashier expects: the same two-step picker in
 * every country.
 */
function LegacyAddressFields({
  draft: a,
  disabled,
  onPatch,
  geo,
  geoSeq,
}: {
  draft: AddressDraft;
  disabled: boolean;
  onPatch: (next: Partial<AddressDraft>) => void;
  geo: GeoAddress | null;
  geoSeq: number;
}) {
  const { t: tr } = useLang();
  return (
    <>
      <CityAreaFields
        draft={a}
        disabled={disabled}
        onPatch={onPatch}
        geo={geo}
        geoSeq={geoSeq}
        areaLabel={tr({ en: 'Area', ar: 'المنطقة' })}
        areaPlaceholder={tr({ en: 'Select area', ar: 'اختر المنطقة' })}
        areaCreateLabel={tr({ en: 'Add area', ar: 'إضافة منطقة' })}
        areaFailedLabel={tr({ en: 'Failed to add area', ar: 'تعذّرت إضافة المنطقة' })}
      />
      <AddrText label={tr({ en: 'Block', ar: 'القطعة' })} value={a.block} onChange={(v) => onPatch({ block: v })} disabled={disabled} />
      <AddrText label={tr({ en: 'Street', ar: 'الشارع' })} value={a.street} onChange={(v) => onPatch({ street: v })} disabled={disabled} />
      <AddrText label={tr({ en: 'Avenue', ar: 'الجادة' })} value={a.avenue} onChange={(v) => onPatch({ avenue: v })} disabled={disabled} />
      <AddrText label={tr({ en: 'House', ar: 'المنزل' })} value={a.house} onChange={(v) => onPatch({ house: v })} disabled={disabled} />
    </>
  );
}

/**
 * City (GL_Governorate) → Area/District (GL_Area), the two lookup combos every
 * address layout starts with. Both are cashier-writable: these tables are
 * per-tenant reference data that is never complete, and a cashier who can't
 * find the value must not be stuck waiting on a back-office edit.
 *
 * Only the labels differ between layouts — الحي (district) reads better under
 * the Saudi National Address, المنطقة (area) everywhere else — so the queries,
 * the city back-fill, the map-lookup matching and the create handlers live here
 * once.
 *
 * `geo` is the address the map lookup last resolved. City and district are the
 * only two parts of it that can't just be pasted into the card: they are
 * foreign keys into per-tenant tables, and Google's name for a place is not one.
 * So they get matched (see matchLookupOption) and, when the tenant has no such
 * row, offered as a one-click "add it" under the combo — never inserted behind
 * the cashier's back.
 */
function CityAreaFields({
  draft: a,
  disabled,
  onPatch,
  geo,
  geoSeq,
  areaLabel,
  areaPlaceholder,
  areaCreateLabel,
  areaFailedLabel,
}: {
  draft: AddressDraft;
  disabled: boolean;
  onPatch: (next: Partial<AddressDraft>) => void;
  geo: GeoAddress | null;
  geoSeq: number;
  areaLabel: string;
  areaPlaceholder: string;
  areaCreateLabel: string;
  areaFailedLabel: string;
}) {
  const { t: tr, lang } = useLang();
  const queryClient = useQueryClient();

  const { data: governorates, isLoading: govLoading } = useGovernorates(a.country_id);
  const { data: districts, isLoading: distLoading } = useDistricts(a.governorate_id);

  // A saved address can carry an area with no city — GovernorateId went unused
  // before this layout existed, and the old flat Area picker never set it.
  // Resolve the city from the area so the card opens fully populated instead of
  // showing "Select a city first" over an area that is set.
  const cityBackfilled = useRef(false);
  useEffect(() => {
    if (cityBackfilled.current) return;
    if (a.governorate_id != null || a.area_id == null) return;
    cityBackfilled.current = true;
    let cancelled = false;
    void customerAPI
      .getAreaNames([a.area_id])
      .then((res) => {
        const govId = res.data[0]?.governorate_id;
        if (!cancelled && govId != null) onPatch({ governorate_id: govId });
      })
      .catch(() => {
        /* Non-fatal: the cashier can still pick the city by hand. */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.area_id, a.governorate_id]);

  // Most Gulf tenants carry exactly one city (villashams_test, binqinwan and
  // egydemo1 all sit under 'العاصمة'), so asking for it would be pure friction.
  // Pick it automatically and let the cashier go straight to the area.
  const soleCitySeeded = useRef(false);
  useEffect(() => {
    if (soleCitySeeded.current) return;
    if (a.governorate_id != null || a.area_id != null) return;
    if (governorates.length !== 1) return;
    const only = governorates[0];
    if (!only) return;
    soleCitySeeded.current = true;
    onPatch({ governorate_id: only.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [governorates, a.governorate_id, a.area_id]);

  // ── Auto-fill from the map lookup ──────────────────────────────────────
  //
  // Runs as a two-step machine because the district list depends on the city:
  // resolve the city first, let useDistricts refetch under the new city, then
  // resolve the district. `pending` holds Google's names plus how far we got;
  // once a step is marked done it never re-runs, so the cashier can override
  // anything we picked without the effect putting it back.
  const [pending, setPending] = useState<GeoPending | null>(null);
  /** Names the tenant has no row for — offered as "add it" under the combo. */
  const [geoMissing, setGeoMissing] = useState<GeoMissing>({ city: null, area: null });
  const appliedSeq = useRef(0);

  useEffect(() => {
    if (geoSeq === 0 || geoSeq === appliedSeq.current) return;
    appliedSeq.current = geoSeq;
    setGeoMissing({ city: null, area: null });
    setPending(geo ? { city: geo.city, district: geo.district, cityDone: false, areaDone: false } : null);
  }, [geoSeq, geo]);

  useEffect(() => {
    if (!pending) return;

    if (!pending.cityDone) {
      if (govLoading) return;
      const match = matchLookupOption(governorates, pending.city);
      if (match) {
        // Changing the city invalidates any district under the old one.
        if (match.id !== a.governorate_id) onPatch({ governorate_id: match.id, area_id: null });
      } else if (pickName(pending.city, lang)) {
        setGeoMissing((m) => ({ ...m, city: pending.city }));
      }
      setPending((p) => (p ? { ...p, cityDone: true } : p));
      return; // let the district list load for the (possibly new) city
    }

    if (!pending.areaDone) {
      if (a.governorate_id == null || distLoading) return;
      const match = matchLookupOption(districts, pending.district);
      if (match) {
        if (match.id !== a.area_id) onPatch({ area_id: match.id });
      } else if (pickName(pending.district, lang)) {
        setGeoMissing((m) => ({ ...m, area: pending.district }));
      }
      setPending((p) => (p ? { ...p, areaDone: true } : p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, governorates, districts, govLoading, distLoading, a.governorate_id]);

  async function createCity(name: string, nameAr?: string | null) {
    try {
      const created = await customerAPI.createGovernorate({
        name,
        name_ar: nameAr ?? null,
        country_id: a.country_id,
      });
      await queryClient.invalidateQueries({ queryKey: ['customers', 'governorates'] });
      return created;
    } catch (err) {
      toast(
        getApiErrorMessage(err) || tr({ en: 'Failed to add city', ar: 'تعذّرت إضافة المدينة' }),
        'error',
      );
      return null;
    }
  }

  async function createArea(name: string, nameAr?: string | null) {
    try {
      const created = await customerAPI.createArea({
        name,
        name_ar: nameAr ?? null,
        governorate_id: a.governorate_id,
      });
      await queryClient.invalidateQueries({ queryKey: ['customers', 'districts'] });
      await queryClient.invalidateQueries({ queryKey: ['customers', 'areas'] });
      return created;
    } catch (err) {
      toast(getApiErrorMessage(err) || areaFailedLabel, 'error');
      return null;
    }
  }

  /**
   * "Add «الملقا»" under a combo the lookup couldn't match. Creates the row
   * with BOTH names Google returned, so the tenant's lookup table gains a
   * properly bilingual entry rather than whichever script the cashier's UI
   * happened to be in.
   */
  async function addMissing(kind: 'city' | 'area', name: Bilingual) {
    const en = name.en?.trim() || null;
    const ar = name.ar?.trim() || null;
    const primary = (lang === 'ar' ? ar : en) ?? en ?? ar;
    if (!primary) return;
    const created =
      kind === 'city' ? await createCity(primary, ar) : await createArea(primary, ar);
    if (!created) return;
    if (kind === 'city') {
      onPatch({ governorate_id: created.id, area_id: null });
      setGeoMissing((m) => ({ ...m, city: null }));
      // The district still has to be matched under the city we just created —
      // it can only have zero rows, so this goes straight to "add it" too.
      setPending((p) => (p ? { ...p, cityDone: true, areaDone: false } : p));
    } else {
      onPatch({ area_id: created.id });
      setGeoMissing((m) => ({ ...m, area: null }));
    }
  }

  return (
    <>
      <div className="add-field">
        <label className="add-label">{tr({ en: 'City', ar: 'المدينة' })}</label>
        <LookupCombo
          value={a.governorate_id}
          options={governorates}
          loading={govLoading}
          disabled={disabled}
          placeholder={tr({ en: 'Select city', ar: 'اختر المدينة' })}
          createLabel={tr({ en: 'Add city', ar: 'إضافة مدينة' })}
          onCreate={createCity}
          onChange={(id) => {
            setGeoMissing((m) => ({ ...m, city: null }));
            // Areas hang off the city, so changing the city drops one that no
            // longer belongs to it.
            onPatch(
              id === a.governorate_id
                ? { governorate_id: id }
                : { governorate_id: id, area_id: null },
            );
          }}
        />
        {geoMissing.city && (
          <GeoAddMissing
            name={geoMissing.city}
            label={tr({ en: 'Add city', ar: 'إضافة مدينة' })}
            disabled={disabled}
            onAdd={() => addMissing('city', geoMissing.city!)}
            onDismiss={() => setGeoMissing((m) => ({ ...m, city: null }))}
          />
        )}
      </div>

      <div className="add-field">
        <label className="add-label">{areaLabel}</label>
        <LookupCombo
          value={a.area_id}
          options={districts}
          loading={distLoading}
          disabled={disabled || a.governorate_id == null}
          placeholder={
            a.governorate_id == null
              ? tr({ en: 'Select a city first', ar: 'اختر المدينة أولًا' })
              : areaPlaceholder
          }
          createLabel={areaCreateLabel}
          onCreate={createArea}
          onChange={(id) => {
            setGeoMissing((m) => ({ ...m, area: null }));
            onPatch({ area_id: id });
          }}
        />
        {geoMissing.area && a.governorate_id != null && (
          <GeoAddMissing
            name={geoMissing.area}
            label={areaCreateLabel}
            disabled={disabled}
            onAdd={() => addMissing('area', geoMissing.area!)}
            onDismiss={() => setGeoMissing((m) => ({ ...m, area: null }))}
          />
        )}
      </div>
    </>
  );
}

/** How far the map lookup has got through the city → district chain. */
interface GeoPending {
  city: Bilingual;
  district: Bilingual;
  cityDone: boolean;
  areaDone: boolean;
}

/** Names the lookup found that the tenant's own tables don't have yet. */
interface GeoMissing {
  city: Bilingual | null;
  area: Bilingual | null;
}

/**
 * "The map says «الملقا», you don't have it — add it?" shown under a combo the
 * lookup couldn't match. An explicit button rather than an automatic insert:
 * GL_Governorate / GL_Area are shared reference data, and a geocoder's name for
 * a neighbourhood is not automatically the name this business files it under.
 */
function GeoAddMissing({
  name,
  label,
  disabled,
  onAdd,
  onDismiss,
}: {
  name: Bilingual;
  label: string;
  disabled: boolean;
  onAdd: () => void;
  onDismiss: () => void;
}) {
  const { t: tr, lang } = useLang();
  const [busy, setBusy] = useState(false);
  const display = pickName(name, lang);
  if (!display) return null;

  return (
    <div className="addr-geo-suggest">
      <span>{tr({ en: 'From the map:', ar: 'من الخريطة:' })} “{display}”</span>
      <button
        type="button"
        disabled={disabled || busy}
        onClick={async () => {
          setBusy(true);
          try {
            await onAdd();
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? tr({ en: 'Adding…', ar: 'جارٍ الإضافة…' }) : label}
      </button>
      <button
        type="button"
        className="dismiss"
        onClick={onDismiss}
        aria-label={tr({ en: 'Dismiss', ar: 'تجاهل' })}
      >
        ×
      </button>
    </div>
  );
}

/**
 * The Saudi National Address (العنوان الوطني) block.
 *
 * Maps onto the legacy schema rather than inventing a parallel one:
 *   المدينة        → GL_Governorate  (Inv_CashCustomerDetail.GovernorateId)
 *   الحي           → GL_Area          (.AreaId) — filtered by the chosen city
 *   الشارع         → .Street
 *   رقم المبنى     → .House
 *   الرقم الفرعي   → .SecondaryNumber  ┐
 *   الرمز البريدي  → .PostalCode       ├ migration 035
 *   العنوان المختصر → .ShortAddressCode ┘
 *
 * العنوان المختصر itself is not rendered here — it is the lookup field at the
 * top of the card (AddressLookupPanel), because typing it is what fills
 * everything below.
 *
 * City and district are cashier-writable: those lookup tables are never
 * complete, so both combos can create the value that was just typed.
 */
function KsaAddressFields({
  draft: a,
  errors,
  disabled,
  onPatch,
  geo,
  geoSeq,
}: {
  draft: AddressDraft;
  errors: AddrFieldErrors;
  disabled: boolean;
  onPatch: (next: Partial<AddressDraft>) => void;
  geo: GeoAddress | null;
  geoSeq: number;
}) {
  const { t: tr } = useLang();

  return (
    <>
      <CityAreaFields
        draft={a}
        disabled={disabled}
        onPatch={onPatch}
        geo={geo}
        geoSeq={geoSeq}
        areaLabel={tr({ en: 'District', ar: 'الحي' })}
        areaPlaceholder={tr({ en: 'Select district', ar: 'اختر الحي' })}
        areaCreateLabel={tr({ en: 'Add district', ar: 'إضافة حي' })}
        areaFailedLabel={tr({ en: 'Failed to add district', ar: 'تعذّرت إضافة الحي' })}
      />

      <AddrText
        label={tr({ en: 'Street', ar: 'الشارع' })}
        value={a.street}
        onChange={(v) => onPatch({ street: v })}
        disabled={disabled}
      />
      <AddrText
        label={tr({ en: 'Building number', ar: 'رقم المبنى' })}
        value={a.house}
        onChange={(v) => onPatch({ house: v })}
        disabled={disabled}
        error={errors.house}
        inputMode="numeric"
        maxLength={4}
      />
      <AddrText
        label={tr({ en: 'Additional number', ar: 'الرقم الفرعي' })}
        value={a.secondary_number}
        onChange={(v) => onPatch({ secondary_number: v })}
        disabled={disabled}
        error={errors.secondary_number}
        inputMode="numeric"
        maxLength={4}
      />
      <AddrText
        label={tr({ en: 'Postal code', ar: 'الرمز البريدي' })}
        value={a.postal_code}
        onChange={(v) => onPatch({ postal_code: v })}
        disabled={disabled}
        error={errors.postal_code}
        inputMode="numeric"
        maxLength={5}
      />
    </>
  );
}

/**
 * One address text field. Intentionally has no placeholder — the label carries
 * the meaning, and example values in the box read as pre-filled data on a busy
 * form. Format hints surface as an error under the field only when what was
 * typed is actually wrong.
 */
function AddrText({
  label,
  value,
  onChange,
  disabled,
  error,
  maxLength = 150,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  error?: string;
  maxLength?: number;
  inputMode?: 'numeric' | 'text';
}) {
  return (
    <div className="add-field">
      <label className="add-label">{label}</label>
      <input
        type="text"
        className={`add-input${error ? ' has-error' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        inputMode={inputMode}
        autoComplete="off"
        disabled={disabled}
        aria-invalid={error ? true : undefined}
      />
      {error && <div className="add-field-error">{error}</div>}
    </div>
  );
}

function FormHeader({ title, sub, onBack }: { title: string; sub: string; onBack: () => void }) {
  const { t: tr } = useLang();
  return (
    <div className="add-form-header">
      <button
        className="add-form-back"
        onClick={onBack}
        title={tr({ en: 'Back', ar: 'رجوع' })}
        aria-label={tr({ en: 'Back', ar: 'رجوع' })}
      >
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>
      <div>
        <div className="add-form-title">{title}</div>
        <div className="add-form-sub">{sub}</div>
      </div>
    </div>
  );
}
